// encrypted-identity-storage-browser.js
// Implements the exact {load, save} interface identity-core.js expects
// from any storage backend (see identity-core.js's own comment on this
// contract) — wraps a real storage backend (IndexedDbStorage) and
// transparently encrypts what flows through it. identity-core.js needs
// zero changes for this to work; it has no idea encryption is happening
// at all, the same way it has no idea whether it's talking to a file or
// IndexedDB.
//
// save() is only ever called once by identity-core.js — the moment a
// brand new identity is generated. That's this module's "setup" moment:
// ask the user which protection method they want (PRF if available,
// passphrase otherwise), establish it, encrypt, persist. load() runs on
// every subsequent visit — re-authenticate (biometric check via PRF, or
// a passphrase prompt) and decrypt.
//
// UI is deliberately NOT built into this file — it takes callback hooks
// so the actual prompts (a passphrase modal, an explanatory screen
// before the WebAuthn ceremony) live in app.js/index.html where the
// rest of this project's UI does, keeping this file testable crypto
// orchestration rather than DOM code.

import { encryptWithPassphrase, decryptWithPassphrase, encryptWithPrfSecret, decryptWithPrfSecret } from './identity-crypto-browser.js'
import { setupPrfCredential, getPrfSecret, isWebAuthnAvailable } from './webauthn-prf-browser.js'

const b64 = (bytes) => btoa(String.fromCharCode(...new Uint8Array(bytes)))
const unb64 = (str) => new Uint8Array(atob(str).split('').map((c) => c.charCodeAt(0)))

export class EncryptedIdentityStorage {
  /**
   * @param {{load: () => Promise<object|null>, save: (data: object) => Promise<void>}} innerStorage
   * @param {object} ui
   * @param {() => Promise<'prf'|'passphrase'|null>} ui.choosePrfOrPassphrase -
   *   only called if PRF is available at all (see isWebAuthnAvailable) —
   *   asks the user which they'd prefer. Returning null cancels setup
   *   (identity-core.js's generateKeyPair work is discarded — the user
   *   can just reload to try again).
   * @param {() => Promise<string|null>} ui.promptNewPassphrase - setup:
   *   ask for and confirm a new passphrase. null cancels.
   * @param {() => Promise<string|null>} ui.promptExistingPassphrase -
   *   unlock: ask for the existing passphrase. null cancels.
   * @param {(error: Error) => Promise<string|null>} ui.promptRetryPassphrase -
   *   called when a previous attempt returned "Incorrect passphrase" —
   *   same shape as promptExistingPassphrase, separated so the UI can
   *   show the error inline rather than a generic prompt.
   */
  constructor (innerStorage, ui) {
    this.inner = innerStorage
    this.ui = ui
  }

  async load () {
    const record = await this.inner.load()
    if (!record) return null // no identity yet — identity-core.js will generate one and call save()

    // Legacy record from before this encryption feature existed — plain
    // {privateKey: base64}, no kdf field at all. Migrate it forward
    // rather than discarding it: keep the exact same key material (same
    // fingerprint, same peer ID — nothing about the user's identity
    // changes), just protect it going forward the same way a brand new
    // identity would be.
    if (record.kdf === undefined && typeof record.privateKey === 'string') {
      const migrated = { privateKey: record.privateKey }
      await this.save(migrated)
      return migrated
    }

    let plaintextBytes
    if (record.kdf === 'webauthn-prf') {
      const prfSecret = await getPrfSecret(unb64(record.credentialId))
      if (!prfSecret) {
        throw new Error('Could not unlock — the authenticator used to set this up is unavailable or the check was cancelled.')
      }
      plaintextBytes = await decryptWithPrfSecret(record, prfSecret)
    } else if (record.kdf === 'pbkdf2') {
      plaintextBytes = await this._decryptWithPassphraseRetry(record)
    } else {
      throw new Error(`Unrecognized identity protection method: ${record.kdf}`)
    }

    return JSON.parse(new TextDecoder().decode(plaintextBytes))
  }

  async _decryptWithPassphraseRetry (record) {
    let passphrase = await this.ui.promptExistingPassphrase()
    if (passphrase == null) throw new Error('Passphrase entry cancelled')

    while (true) {
      try {
        return await decryptWithPassphrase(record, passphrase)
      } catch (err) {
        passphrase = await this.ui.promptRetryPassphrase(err)
        if (passphrase == null) throw new Error('Passphrase entry cancelled')
      }
    }
  }

  async save (data) {
    const plaintextBytes = new TextEncoder().encode(JSON.stringify(data))

    let method = 'passphrase'
    if (isWebAuthnAvailable()) {
      const chosen = await this.ui.choosePrfOrPassphrase()
      if (chosen == null) throw new Error('Identity setup cancelled')
      method = chosen
    }

    if (method === 'prf') {
      const setup = await setupPrfCredential('Station Identity')
      if (setup) {
        const record = await encryptWithPrfSecret(plaintextBytes, setup.prfSecret)
        record.credentialId = b64(setup.credentialId)
        await this.inner.save(record)
        return
      }
      // Authenticator didn't actually support PRF despite WebAuthn being
      // present at all (see webauthn-prf-browser.js's comment on why
      // this can't be known for certain ahead of time) — fall through
      // to the passphrase path rather than leaving the identity
      // unprotected.
    }

    const passphrase = await this.ui.promptNewPassphrase()
    if (passphrase == null) throw new Error('Identity setup cancelled')
    const record = await encryptWithPassphrase(plaintextBytes, passphrase)
    await this.inner.save(record)
  }
}
